import { getCookieOrEmptyString } from "@multimediallc/web-utils/storage"
import { createSlice } from "@reduxjs/toolkit"
import { createApi } from "@reduxjs/toolkit/query/react"
import { cbBaseQuery } from "../utils/myFetch"
import type { RootState } from "./store"
import type { Message, Thread } from "../components/messaging/types"
import type { IUserInfo } from "@multimediallc/web-utils/types"
import type { PayloadAction, UnknownAction } from "@reduxjs/toolkit"
import type { WritableDraft } from "immer"
import type { ThunkAction } from "redux-thunk"

enum CacheUpdateType {
    Default = "DEFAULT",
    Search = "SEARCH",
}

declare global {
    interface Window {
        PRODUCTION: boolean
    }
}

interface MessageResponse {
    messages: Message[]
    num_unread: number
    has_more: boolean
}

export interface ThreadsResponse {
    threads: Thread[]
    has_more: boolean
    has_conversation_features: boolean
}

interface ThreadsQueryParams {
    offset: number
    search_username?: string
}

export const DM_THREAD_PAGE_LIMIT = 20

export const DEFAULT_THREAD_PARAMS: ThreadsQueryParams = {
    offset: 0,
}

interface MessagingState {
    conversationListScroll: number
    initialUnread: { [username: string]: number }
    newConversationSearchSource: "icon" | "button" | null
    currentSearchUsername: string | null
}

const initialState: MessagingState = {
    conversationListScroll: 0,
    initialUnread: {},
    newConversationSearchSource: null,
    currentSearchUsername: null,
}

export const messagingSlice = createSlice({
    name: "messaging",
    initialState,
    reducers: {
        setConversationListScroll: (state, action: PayloadAction<number>) => {
            state.conversationListScroll = action.payload
        },
        setInitialUnread: (
            state,
            action: PayloadAction<{ username: string; count: number }>,
        ) => {
            state.initialUnread[action.payload.username] = action.payload.count
        },
        setNewConversationSearchSource: (
            state,
            action: PayloadAction<"icon" | "button" | null>,
        ) => {
            state.newConversationSearchSource = action.payload
        },
        setCurrentSearchUsername: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.currentSearchUsername = action.payload
        },
    },
})

export const {
    setConversationListScroll,
    setInitialUnread,
    setNewConversationSearchSource,
    setCurrentSearchUsername,
} = messagingSlice.actions
export default messagingSlice.reducer

interface PublishMessageRequest {
    message: string
    to_user: string
    from_user: string
}

interface PublishMessageResponse {
    status: string
    is_reply?: boolean
    token_color?: string
    in_fanclub?: boolean
    sig?: string
}

const transformUserInfo = (user: any): IUserInfo => ({
    username: user.username,
    isBroadcaster: user.is_broadcaster,
    inFanclub: user.in_fanclub,
    isFollowing: user.is_following,
    hasTokens: user.has_tokens,
    isMod: user.is_mod,
    tippedRecently: user.tipped_recently,
    tippedALotRecently: user.tipped_alot_recently,
    tippedTonsRecently: user.tipped_tons_recently,
    gender: user.gender,
})

export const messagingApi = createApi({
    reducerPath: "messagingApi",
    tagTypes: ["History", "Threads"],
    baseQuery: cbBaseQuery({
        baseUrl: "/api/messaging/",
    }),
    endpoints: (builder) => ({
        getUnread: builder.query<any, void>({
            query: () => ({
                url: "unread/",
                method: "GET",
            }),
        }),
        getThreads: builder.query<
            ThreadsResponse,
            { offset: number; search_username?: string }
        >({
            query: ({ offset, search_username }) => ({
                url: search_username
                    ? `threads/?offset=${offset}&search_username=${search_username}`
                    : `threads/?offset=${offset}`,
                method: "GET",
            }),
            transformResponse: (response: any) => {
                if (!response) return response

                const transformed = { ...response }
                if (transformed.threads) {
                    transformed.threads = transformed.threads.map(
                        (thread: any) => ({
                            ...thread,
                            other_user: thread.other_user
                                ? transformUserInfo(thread.other_user)
                                : undefined,
                        }),
                    )
                }
                return transformed
            },
            providesTags: () => [{ type: "Threads", id: "LIST" }],
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return `${endpointName}:${queryArgs.search_username || ""}`
            },
            merge: (currentCache, newItems, { arg }) => {
                if (arg.offset === 0) {
                    return newItems
                }

                const mergedThreads = [...currentCache.threads]

                newItems.threads.forEach((newThread) => {
                    const existingIndex = mergedThreads.findIndex(
                        (thread) =>
                            thread?.other_user?.username ===
                            newThread?.other_user?.username,
                    )
                    if (existingIndex !== -1) {
                        mergedThreads[existingIndex] = newThread
                    } else {
                        mergedThreads.push(newThread)
                    }
                })

                return {
                    threads: mergedThreads,
                    has_more: newItems.has_more,
                    has_conversation_features:
                        newItems.has_conversation_features,
                }
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg?.offset !== previousArg?.offset
            },
        }),
        publishMessage: builder.mutation<
            PublishMessageResponse,
            PublishMessageRequest
        >({
            query: (data) => ({
                url: "publish/",
                method: "POST",
                data: {
                    csrfmiddlewaretoken: getCookieOrEmptyString("csrftoken"),
                    message: JSON.stringify({ m: data.message }),
                    to_user: data.to_user,
                    from_user: data.from_user,
                },
            }),
        }),
        getHistory: builder.query<
            MessageResponse,
            { username: string; offset?: string }
        >({
            query: ({ username, offset }) => ({
                url: `history/${username}/${offset ? `?offset=${offset}` : ""}`,
                method: "GET",
            }),
            transformResponse: (response: any) => {
                if (!response) return response

                const transformed = { ...response }
                if (transformed.messages) {
                    transformed.messages = transformed.messages.map(
                        (message: any) => ({
                            ...message,
                            from_user: message.from_user
                                ? transformUserInfo(message.from_user)
                                : undefined,
                        }),
                    )
                }
                return transformed
            },
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return `${endpointName}:${queryArgs.username}`
            },
            merge: (currentCache, newItems, { arg }) => {
                if (newItems.messages === undefined) {
                    newItems.messages = []
                }

                if (arg.offset) {
                    // Pagination calls
                    return {
                        messages: [
                            ...newItems.messages,
                            ...(currentCache.messages || []),
                        ],
                        num_unread: currentCache.num_unread,
                        has_more: newItems.has_more,
                    }
                }

                // Non-offset calls (initial load/refetch).
                const currentMessagesFromCache = currentCache.messages || []
                const newMessagesFromServer = newItems.messages || []

                const messageMap = new Map<string, Message>()

                currentMessagesFromCache.forEach((msg) => {
                    if (msg?.i) {
                        messageMap.set(msg.i, msg)
                    }
                })

                newMessagesFromServer.forEach((msg) => {
                    if (msg?.i) {
                        messageMap.set(msg.i, msg)
                    }
                })

                const combinedAndDeDupedMessages = Array.from(
                    messageMap.values(),
                ).sort((a, b) => a.created_at - b.created_at)

                let mergedHasMore: boolean
                if (currentCache.has_more === false) {
                    // If cache already indicated all history was loaded (has_more: false),
                    // maintain this status, even if a page 1 refetch (newItems) says has_more: true.
                    mergedHasMore = false
                } else {
                    // Otherwise, take the has_more status from the latest server response.
                    mergedHasMore = newItems.has_more
                }

                return {
                    messages: combinedAndDeDupedMessages,
                    num_unread: newItems.num_unread,
                    has_more: mergedHasMore,
                }
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg?.offset !== previousArg?.offset
            },
            providesTags: (result, error, { username }) => [
                { type: "History" as const, id: username },
            ],
        }),
        getProfile: builder.query<any, string>({
            query: (otherUser) => ({
                url: `profile/${otherUser}/`,
                method: "GET",
            }),
            transformResponse: (response: any) => {
                if (!response) return response

                const transformed = { ...response }
                if (transformed.user) {
                    transformed.user = transformUserInfo(transformed.user)
                }
                if (transformed.sitewide_user) {
                    transformed.sitewide_user = transformUserInfo(
                        transformed.sitewide_user,
                    )
                }
                return transformed
            },
        }),
        markAsRead: builder.mutation<void, string>({
            query: (username) => ({
                url: "last-seen/",
                method: "POST",
                data: {
                    from_usernames: username,
                },
            }),
            onQueryStarted: async (
                username,
                {
                    dispatch,
                    queryFulfilled,
                    getState,
                }: {
                    dispatch: any
                    getState: () => any
                    queryFulfilled: Promise<{ data: void }>
                },
            ) => {
                let originalUnreadCount = 0
                try {
                    const state = getState()
                    const defaultThreadsData =
                        messagingApi.endpoints.getThreads.select(
                            DEFAULT_THREAD_PARAMS,
                        )(state)?.data

                    const thread = defaultThreadsData?.threads?.find(
                        (t) => t?.other_user?.username === username,
                    )
                    originalUnreadCount = thread?.num_unread || 0

                    dispatch(updateThreadUnread(username, 0))

                    await queryFulfilled
                } catch {
                    dispatch(updateThreadUnread(username, originalUnreadCount))
                }
            },
        }),
        typingIndicator: builder.mutation<void, { to_username: string }>({
            query: (data) => ({
                url: "typing/",
                method: "POST",
                data: {
                    csrfmiddlewaretoken: getCookieOrEmptyString("csrftoken"),
                    to_username: data.to_username,
                },
            }),
        }),
        deleteConversation: builder.mutation<void, { to_username: string }>({
            query: (data) => ({
                url: "delete-conversation/",
                method: "POST",
                data: {
                    csrfmiddlewaretoken: getCookieOrEmptyString("csrftoken"),
                    to_username: data.to_username,
                },
            }),
        }),
        updateReaction: builder.mutation<
            void,
            { message_id: string; reaction: string }
        >({
            query: (data) => ({
                url: `reactions/${data.message_id}/`,
                method: "POST",
                data: {
                    csrfmiddlewaretoken: getCookieOrEmptyString("csrftoken"),
                    reaction: data.reaction,
                },
            }),
        }),
        deleteReaction: builder.mutation<void, { message_id: string }>({
            query: (data) => ({
                url: `reactions/${data.message_id}/`,
                method: "DELETE",
                data: {
                    csrfmiddlewaretoken: getCookieOrEmptyString("csrftoken"),
                },
            }),
        }),
    }),
})

type UpdateQueryDataResult = ReturnType<
    typeof messagingApi.util.updateQueryData
>

type ThreadsDraftUpdater<TArgs = void> = (
    draft: WritableDraft<ThreadsResponse>,
    args: TArgs,
    cacheType: CacheUpdateType,
) => void

/**
 * Thunk helper that applies cache modifications to both default
 * and current search results for the 'getThreads' endpoint.
 * @param updater - Function that modifies the cache draft.
 * @param updaterArgs - Arguments for the updater function.
 */
const updateThreadCaches = <TArgs>(
    updater: ThreadsDraftUpdater<TArgs>,
    updaterArgs: TArgs,
): ThunkAction<void, RootState, unknown, UnknownAction> => {
    return (dispatch, getState) => {
        const { currentSearchUsername } = getState().messaging

        dispatch(
            messagingApi.util.updateQueryData(
                "getThreads",
                DEFAULT_THREAD_PARAMS,
                (draft) => {
                    if (draft) {
                        updater(draft, updaterArgs, CacheUpdateType.Default)
                    }
                },
            ),
        )

        if (currentSearchUsername) {
            dispatch(
                messagingApi.util.updateQueryData(
                    "getThreads",
                    { offset: 0, search_username: currentSearchUsername },
                    (draft) => {
                        if (draft) {
                            updater(draft, updaterArgs, CacheUpdateType.Search)
                        }
                    },
                ),
            )
        }
    }
}

const upsertThreadUpdater: ThreadsDraftUpdater<{
    newThread: Thread
    isFromMe: boolean
}> = (draft, { newThread, isFromMe }, cacheType) => {
    const existingThreadIdx = draft.threads.findIndex(
        (thread) =>
            thread?.other_user?.username === newThread?.other_user?.username,
    )

    if (cacheType === CacheUpdateType.Search && existingThreadIdx === -1) {
        // Don't insert thread into search results if it wasn't already there
        return
    }

    let calculatedUnreadCount: number

    if (existingThreadIdx !== -1) {
        const [existingThread] = draft.threads.splice(existingThreadIdx, 1)
        calculatedUnreadCount = isFromMe
            ? 0
            : (existingThread?.num_unread || 0) + 1
    } else {
        calculatedUnreadCount = isFromMe ? 0 : newThread.num_unread || 0
    }

    const threadToInsert = {
        ...newThread,
        num_unread: calculatedUnreadCount,
    }

    draft.threads.unshift(threadToInsert)
}

/** Adds or updates a thread in the 'getThreads' cache (both inbox and current search). */
export const upsertThread = (
    newThread: Thread,
    isFromMe: boolean,
): ThunkAction<void, RootState, unknown, UnknownAction> => {
    return updateThreadCaches(upsertThreadUpdater, { newThread, isFromMe })
}

const setThreadUnreadUpdater: ThreadsDraftUpdater<{
    username: string
    numUnread: number
}> = (draft, { username, numUnread }, cacheType) => {
    const thread = draft.threads.find(
        (t) => t?.other_user?.username === username,
    )
    if (thread) {
        thread.num_unread = numUnread
    }
}

/** Sets the unread count for a thread in the 'getThreads' cache (both inbox and current search). */
export const updateThreadUnread = (
    username: string,
    numUnread: number,
): ThunkAction<void, RootState, unknown, UnknownAction> => {
    return updateThreadCaches(setThreadUnreadUpdater, { username, numUnread })
}

const updateThreadUserInfoUpdater: ThreadsDraftUpdater<{
    username: string
    userInfo: Partial<IUserInfo>
}> = (draft, { username, userInfo }, cacheType) => {
    const thread = draft.threads.find(
        (t) => t?.other_user?.username === username,
    )

    if (thread?.other_user) {
        thread.other_user = { ...thread.other_user, ...userInfo }
    }
}

/** Updates the user info for a thread in the 'getThreads' cache (both inbox and current search). */
export const updateThreadUserInfo = (
    username: string,
    userInfo: Partial<IUserInfo>,
): ThunkAction<void, RootState, unknown, UnknownAction> => {
    return updateThreadCaches(updateThreadUserInfoUpdater, {
        username,
        userInfo,
    })
}

export const addMessageToHistory = (
    username: string,
    newMessage: Message,
): UpdateQueryDataResult => {
    return messagingApi.util.updateQueryData(
        "getHistory",
        { username },
        (draft) => {
            if (draft.messages === undefined) {
                draft.messages = [newMessage]
            } else {
                draft.messages.push(newMessage)
            }
            return draft
        },
    )
}

export const updateMessageReactions = (
    username: string,
    messageId: string,
    reactions: Record<string, string>,
): UpdateQueryDataResult => {
    return messagingApi.util.updateQueryData(
        "getHistory",
        { username },
        (draft) => {
            const message = draft.messages.find((m) => m.i === messageId)
            if (message) {
                message.reactions = reactions
            }
        },
    )
}

const removeThreadUpdater: ThreadsDraftUpdater<{ username: string }> = (
    draft,
    { username },
    cacheType,
) => {
    const index = draft.threads.findIndex(
        (thread) => thread?.other_user?.username === username,
    )
    if (index >= 0) {
        draft.threads.splice(index, 1)
    }
}

/** Removes a thread from the 'getThreads' cache (both inbox and current search). */
export const removeThread = ({
    username,
}: {
    username: string
}): ThunkAction<void, RootState, unknown, UnknownAction> => {
    return updateThreadCaches(removeThreadUpdater, { username })
}

/**
 * Updates the cached data for the 'getProfile' endpoint for a specific user.
 * @param username - The username whose profile cache should be updated.
 * @param updatedUserData - Partial user data containing the fields to update.
 */
export const updateProfileCache = (
    username: string,
    updatedUserData: Partial<IUserInfo>,
): ThunkAction<void, RootState, unknown, UnknownAction> => {
    return (dispatch) => {
        dispatch(
            messagingApi.util.updateQueryData(
                "getProfile",
                username,
                (draft) => {
                    if (draft?.user) {
                        Object.assign(draft.user, updatedUserData)
                    }
                },
            ),
        )
    }
}

export const {
    useGetUnreadQuery,
    useGetThreadsQuery,
    useLazyGetThreadsQuery,
    usePublishMessageMutation,
    useGetHistoryQuery,
    useLazyGetHistoryQuery,
    useGetProfileQuery,
    useMarkAsReadMutation,
    useTypingIndicatorMutation,
    useDeleteConversationMutation,
    useUpdateReactionMutation,
    useDeleteReactionMutation,
} = messagingApi
